import { useAuth } from '../contexts/AuthContext';
import { Permission } from '../types/auth';

/**
 * Hook to check if user has a specific permission
 * @param permission - The permission to check
 * @returns boolean indicating if user has the permission
 */
export const usePermission = (permission: Permission): boolean => {
  const { hasPermission } = useAuth();
  return hasPermission(permission);
};

/**
 * Hook to check if user can perform an action
 * @param action - The action to check (e.g., 'deploy:agent')
 * @param resource - Optional resource ID for fine-grained checks
 * @returns boolean indicating if user can perform the action
 */
export const useCan = (action: string, resource?: string): boolean => {
  const { can } = useAuth();
  return can(action, resource);
};

/**
 * Hook to check if user can view DLP data (ABAC with data classification)
 * @returns boolean indicating if user can view DLP data
 */
export const useCanViewDLP = (): boolean => {
  const { canViewDLPData } = useAuth();
  return canViewDLPData();
};

/**
 * Hook to get current user's attributes
 */
export const useUserAttributes = () => {
  const { userAttributes } = useAuth();
  return userAttributes;
};

/**
 * Hook to check if user has admin or approver role
 */
export const useIsApprover = (): boolean => {
  const userAttributes = useUserAttributes();
  if (!userAttributes) return false;
  return userAttributes.role === 'Admin' || userAttributes.role === 'SecurityOfficer';
};
